/**
 * useSocialChannels
 *
 * Composable providing reactive social channel connection state and utilities.
 * Replaces the social-connection computed properties and `getChannelFromPageActive`
 * method from the global mixin.
 *
 * Reads from `useAuthStore().socialConnections` which is pre-populated at startup
 * by `checkToken()` via TanStack Query.
 */
import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'

export function useSocialChannels() {
  const authStore = useAuthStore()

  // ---------------------------------------------------------------------------
  // Raw connections data
  // ---------------------------------------------------------------------------

  const _socialConnectionsData = computed(() => {
    return authStore.socialConnections ?? { whatsapp: [], googlercs: [], sms: [], email: [] }
  })

  // ---------------------------------------------------------------------------
  // Connection status
  // ---------------------------------------------------------------------------

  const whatsappConnected = computed(() => (_socialConnectionsData.value.whatsapp?.length ?? 0) > 0)
  const googlercsConnected = computed(() => (_socialConnectionsData.value.googlercs?.length ?? 0) > 0)
  const smsConnected = computed(() => (_socialConnectionsData.value.sms?.length ?? 0) > 0)
  const emailConnected = computed(() => (_socialConnectionsData.value.email?.length ?? 0) > 0)

  // ---------------------------------------------------------------------------
  // First-channel data objects
  // ---------------------------------------------------------------------------

  const whatsAppSocialData = computed(() => _socialConnectionsData.value.whatsapp?.[0] ?? null)
  const googlercsSocialData = computed(() => _socialConnectionsData.value.googlercs?.[0] ?? null)
  const smsSocialData = computed(() => _socialConnectionsData.value.sms?.[0] ?? null)
  const emailSocialData = computed(() => _socialConnectionsData.value.email?.[0] ?? null)

  // ---------------------------------------------------------------------------
  // Aggregated helpers
  // ---------------------------------------------------------------------------

  const socialConnectedChannels = computed<string[]>(() => {
    const channels: string[] = []
    if (whatsappConnected.value) channels.push('whatsapp')
    if (googlercsConnected.value) channels.push('googlercs')
    if (smsConnected.value) channels.push('sms')
    if (emailConnected.value) channels.push('email')
    return channels
  })

  /** `true` when at least one social channel is connected. */
  const socialPlatformConnected = computed(
    () => whatsappConnected.value || googlercsConnected.value || smsConnected.value || emailConnected.value
  )

  /**
   * Alias for `socialConnectedChannels` — kept for backward-compat with
   * components that used `socialPlatformConnectedArray` from the mixin.
   */
  const socialPlatformConnectedArray = socialConnectedChannels

  // ---------------------------------------------------------------------------
  // Channel resolution
  // ---------------------------------------------------------------------------

  /**
   * Resolves the channel name from a `pageActive` identifier (page ID, business ID,
   * account name, mobile number, or the literal channel slug).
   *
   * @param pageActive - Identifier to match against connected channel data.
   *                     Pass `''` / `'None'` / `null` to fall back to 'n/a'.
   * @param strictBool - When `true`, returns `'INVALID_MERCHANT'` if no match is found.
  * @returns Channel slug ('whatsapp' | 'googlercs' | 'sms' | 'email') or 'n/a' / 'INVALID_MERCHANT'.
   */
  function getChannelFromPageActive(pageActive: string | null | undefined, strictBool?: boolean): string {
    if (typeof pageActive === 'undefined' || pageActive === '' || pageActive === 'None' || pageActive === null) {
      return 'n/a'
    }

    let channel = ''

    const waConnections = _socialConnectionsData.value.whatsapp ?? []
    const rcsConnections = _socialConnectionsData.value.googlercs ?? []
    const smsConnections = _socialConnectionsData.value.sms ?? []
    const emailConnections = _socialConnectionsData.value.email ?? []

    if (
      pageActive === 'whatsapp' ||
      waConnections.some((wa) => wa && (pageActive === wa.id.toString() || pageActive === wa.business_id))
    ) {
      if (whatsappConnected.value) channel = 'whatsapp'
    } else if (
      pageActive === 'googlercs' ||
      rcsConnections.some((rcs) => rcs && pageActive === rcs.id.toString())
    ) {
      if (googlercsConnected.value) channel = 'googlercs'
    } else if (
      pageActive === 'sms' ||
      smsConnections.some(
        (sms) =>
          sms &&
          (pageActive === sms.account_name ||
            pageActive === sms.id.toString() ||
            pageActive === sms.mobile_number)
      )
    ) {
      if (smsConnected.value) channel = 'sms'
    } else if (
      pageActive === 'email' ||
      emailConnections.some(
        (email) =>
          email &&
          (pageActive === email.account_name ||
            pageActive === email.id?.toString() ||
            pageActive === email.sender_email)
      )
    ) {
      if (emailConnected.value) channel = 'email'
    } else if (strictBool) {
      channel = 'INVALID_MERCHANT'
    }

    return channel
  }

  return {
    // status
    whatsappConnected,
    googlercsConnected,
    smsConnected,
    emailConnected,
    // data objects
    whatsAppSocialData,
    googlercsSocialData,
    smsSocialData,
    emailSocialData,
    // aggregated
    socialConnectedChannels,
    socialPlatformConnected,
    socialPlatformConnectedArray,
    // method
    getChannelFromPageActive,
  }
}
